import { createApp } from 'vue';
import '@/assets/scss/theme.scss';
import App from './App.vue';
import '@/utils/logger-config';
import Logger from 'js-logger';
import router from '@/router';
import Notifications from '@kyvg/vue3-notification';
import { createBootstrap } from 'bootstrap-vue-next';
import i18n, { initializeLanguage } from './i18n';
import posthog from 'posthog-js';
import { st, maybeSt } from './i18n/localized-string';
import {
    init as sentryInit,
    captureConsoleIntegration,
    browserTracingIntegration,
    replayIntegration,
    browserProfilingIntegration,
    captureException,
    startInactiveSpan,
    addBreadcrumb,
} from '@sentry/vue';
import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseUrl } from './services/supabase';
import { supabaseIntegration } from '@supabase/sentry-js-integration';
import VueDOMPurifyHTML from 'vue-dompurify-html';

const app = createApp(App);

initializeLanguage();

app.use(router);
app.use(i18n);
// b-button's :loading state renders a hardcoded "Loading..." unless loadingText is set; a getter
// keeps it re-evaluating against the active locale.
app.use(
    createBootstrap({
        components: {
            BButton: {
                get loadingText() {
                    return i18n.global.t('loadingDots');
                },
            },
        },
    }),
);
app.use(Notifications);
app.use(VueDOMPurifyHTML);

app.use(({ config }) => {
    config.globalProperties.$st = st;
    config.globalProperties.$maybeSt = maybeSt;
});

const posthogApiKey = import.meta.env.VITE_POSTHOG_API_KEY ?? '';
if (posthogApiKey !== '') {
    posthog.init(posthogApiKey, { api_host: 'https://eu.posthog.com' });
}

const dsn = import.meta.env.VITE_SENTRY_DSN ?? '';
if (dsn !== '') {
    sentryInit({
        app,
        dsn,
        enabled: import.meta.env.PROD,
        environment: import.meta.env.MODE || import.meta.env.APP_ENV,
        release: import.meta.env.APP_VERSION,
        integrations: [
            captureConsoleIntegration({
                levels: ['error'], // Capture console.error and console.warn
            }),
            supabaseIntegration(
                SupabaseClient,
                { startInactiveSpan, captureException, addBreadcrumb },
                {
                    tracing: true,
                    breadcrumbs: true,
                    errors: true,
                },
            ),
            browserTracingIntegration({
                router,
                shouldCreateSpanForRequest: (url) => !url.startsWith(`${supabaseUrl}/rest`),
            }),
            replayIntegration({
                maskAllText: true, // mask all text content for PII protection
                blockAllMedia: false,
                maskAllInputs: true, // mask form inputs (passwords, sensitive fields)
            }),
            browserProfilingIntegration(),
        ],
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        profilesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // JS call stack profiles for slow transactions
        replaysSessionSampleRate: 0.1, // 10% of sessions
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    });

    // Properly handle Vue component errors in production
    if (import.meta.env.PROD) {
        app.config.warnHandler = () => null; // we don't want to see any warnings in production sentry logs
        app.config.errorHandler = (err, instance, info) => {
            // Send to Sentry with Vue context
            captureException(err, {
                contexts: {
                    vue: {
                        componentName: instance?.$options?.name || 'Unknown',
                        lifeCycleHook: info,
                    },
                },
            });
        };
    } else if (import.meta.env.DEV) {
        Logger.get('Main').log('Running in Development Mode');
    }
}

app.mount('#app');
